/* Runtime per-kitchen theming.
 *
 * The design tokens resolve brand and accent colors through raw vars on
 * :root (see styles/tokens.css). Overriding those vars inline on <html>
 * retints every derived utility without a re-render. Inline styles outrank
 * the stylesheet's dark-theme blocks, so the ramps are recomputed for the
 * effective theme and re-applied whenever the system preference flips.
 */

import {
  deriveAccentRamp,
  deriveBrandDarkRamp,
  deriveBrandRamp,
} from '@/lib/colors'

import { useKitchenStore } from './kitchenStore'

const DARK_CANVAS = '#0e1712'

function prefersDark(): boolean {
  const forced = document.documentElement.dataset.theme
  if (forced === 'dark') return true
  if (forced === 'light') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function applyBrandTheme(brandPrimary: string | null | undefined): void {
  const dark = prefersDark()
  const ramp = dark
    ? deriveBrandDarkRamp(brandPrimary)
    : deriveBrandRamp(brandPrimary)
  const accent = deriveAccentRamp(brandPrimary, dark ? 'dark' : 'light')
  const root = document.documentElement.style
  root.setProperty('--brand', ramp.brand)
  root.setProperty('--brand-strong', ramp.brandStrong)
  root.setProperty('--brand-pressed', ramp.brandPressed)
  root.setProperty('--brand-tint', ramp.brandTint)
  root.setProperty('--brand-tint-strong', ramp.brandTintStrong)
  root.setProperty('--on-brand', ramp.onBrand)
  root.setProperty('--brand-on-tint', ramp.brandOnTint)
  root.setProperty('--accent', accent.accent)
  root.setProperty('--accent-tint', accent.accentTint)
  root.setProperty('--accent-strong', accent.accentStrong)
  root.setProperty('--on-accent', accent.onAccent)
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', dark ? DARK_CANVAS : ramp.brand)
}

/* Payment sheets render on the provider's light chrome regardless of the
   app theme, so they always get the light-ramp brand. */
export function currentBrandHex(): string {
  return deriveBrandRamp(useKitchenStore.getState().brandPrimary).brand
}

/* Apply the persisted brand before first paint, follow store changes, and
   re-derive the ramps when the system color scheme flips. */
export function initTheme(): void {
  applyBrandTheme(useKitchenStore.getState().brandPrimary)
  useKitchenStore.subscribe((state, previous) => {
    if (state.brandPrimary !== previous.brandPrimary) {
      applyBrandTheme(state.brandPrimary)
    }
  })
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      applyBrandTheme(useKitchenStore.getState().brandPrimary)
    })
}
